import { SAAS_BASE_PLAN, MODULE_PRICE } from '../../services/saas-pricing';

/**
 * Soluciones por sector (fuente única en código para el listado `/soluciones` y
 * el detalle `/soluciones/:slug`). Cada solución es la misma base + una
 * preselección de módulos del catálogo, a los mismos precios (modelo de
 * `saas-pricing.ts`): un pack de sector nunca crea precio ni lógica nueva, solo
 * preselecciona. El detalle es una plantilla común; solo cambia el contenido.
 *
 * Los `presetMatchers` alimentan el deep-link a la prueba
 * (`/prueba-sin-compromiso?sol=<slug>`), que pre-marca esos módulos en el
 * configurador (mismo mecanismo que las plantillas de `SECTOR_PRESETS`).
 */

/** Precio estándar de un complemento (€/mes, precio único). Ver saas-pricing. */
export const COMPLEMENT_PRICE = 3;

/** Base Esencial: común a todas las soluciones (hexágono completo, teal). */
export const SOLUTION_BASE = {
  label: 'Base Esencial',
  price: SAAS_BASE_PLAN.monthly, // 45 €
  color: 'var(--ts-primary)',
  desc: 'Fichaje, facturación, clientes, gastos y calendario de fechas clave. Operativos ilimitados y 1 usuario completo.',
  note: 'No cobramos licencia por quien solo ficha',
};

/** Complemento de un módulo (cuelga de él; se dibuja en contorno). */
export interface SolutionComplement {
  label: string;
  price: number;
  /** Token de color de la pieza (hereda el color de su módulo padre). */
  color: string;
  desc: string;
}

/** Módulo preseleccionado de una solución (pieza suelta en su color). */
export interface SolutionModuleLine {
  label: string;
  price: number;
  color: string;
  desc: string;
  complement?: SolutionComplement;
}

export interface SolutionTimelineStep {
  /** Hito ("08:00", "Cierre", "Cada mes"…). */
  time: string;
  title: string;
  text: string;
}

export interface SolutionCase {
  /** Titular del ejemplo de uso. */
  title: string;
  text: string;
  /** Etiqueta pequeña con los módulos implicados. */
  tags?: string;
  /** Slug del caso real en `/proyectos/:slug` ("Ver el caso completo →"). */
  caseSlug?: string;
  /** Pista de la captura pendiente. */
  imageHint: string;
}

export interface SolutionImages {
  /** Fila del listado (solo soluciones destacadas). */
  listado?: string;
  /** Hero del detalle. */
  hero?: string;
  /** Ejemplo de uso del detalle. */
  caso?: string;
}

export interface Solution {
  slug: string;
  /** Nombre del sector (breadcrumb, cards, chips). */
  sector: string;
  /** Fila destacada con imagen (true) o card compacta (false) en el listado. */
  featured: boolean;

  // ── Listado ──────────────────────────────────────────────────────────
  listingTagline: string;
  listingImageHint: string;

  // ── Detalle: hero ────────────────────────────────────────────────────
  heroTitle: string;
  heroSubtitle: string;
  heroImageHint: string;

  // ── Detalle: desglose (la base va implícita) ─────────────────────────
  modules: SolutionModuleLine[];

  // ── Detalle: día a día + caso ────────────────────────────────────────
  timeline: SolutionTimelineStep[];
  case: SolutionCase;

  // ── Detalle: CTA final ───────────────────────────────────────────────
  ctaTitle: string;

  // ── Capturas (fallback al marcador si el archivo no existe) ───────────
  images: SolutionImages;

  // ── Integración con la prueba ────────────────────────────────────────
  presetMatchers: RegExp[];
}

// Colores del diccionario fijo (styles.scss → --ts-piece-*).
const C_PARTES = 'var(--ts-piece-partes)';
const C_PROYECTOS = 'var(--ts-piece-proyectos)';
const C_PORTAL = 'var(--ts-piece-portal)';
const C_TPV = 'var(--ts-piece-tpv)';
const C_INVENTARIO = 'var(--ts-piece-inventario)';
const C_WEB = 'var(--ts-piece-web)';
const C_RESTAURACION = 'var(--ts-piece-restauracion)';

const M = MODULE_PRICE; // 8
const K = COMPLEMENT_PRICE; // 3

// Capturas reales del producto (src/assets/img/soluciones). Nombradas por
// pantalla; se asignan a cada hueco según encaje con el sector. Si el archivo
// no existe, `app-image-slot` cae al marcador.
const asset = (name: string) => `assets/img/soluciones/${name}`;

export const SOLUTIONS: Solution[] = [
  // ── Construcción y obra ────────────────────────────────────────────────
  {
    slug: 'construccion-obra',
    sector: 'Construcción y obra',
    featured: true,
    listingTagline:
      'Los partes se firman en la obra desde el móvil y pasan a factura sin copiar nada. Dirección ve cada obra con sus costes frente al presupuesto.',
    listingImageHint: 'Captura real: parte de obra en el móvil',
    heroTitle: 'La obra se firma a pie de tajo. La oficina lo ve al instante.',
    heroSubtitle:
      'Partes de obra desde el móvil que pasan a factura sin recopiar, y cada obra con sus costes frente al presupuesto en un mismo panel.',
    heroImageHint: 'Captura real: la app en la obra',
    modules: [
      {
        label: 'Partes de trabajo',
        price: M,
        color: C_PARTES,
        desc: 'Partes de obra firmados a pie de tajo: materiales, horas y avances, sin copiar nada a mano.',
      },
      {
        label: 'Proyectos',
        price: M,
        color: C_PROYECTOS,
        desc: 'Cada obra con su presupuesto, sus costes y su avance. Dirección ve al momento cómo va frente a lo previsto.',
      },
    ],
    timeline: [
      {
        time: '07:30',
        title: 'El tajo, en el móvil',
        text: 'El equipo ve la obra del día, sus tareas y los materiales previstos antes de empezar.',
      },
      {
        time: '13:00',
        title: 'Parte firmado sobre el terreno',
        text: 'Horas, materiales y avance quedan registrados y firmados sin volver a la oficina.',
      },
      {
        time: '17:30',
        title: 'Costes contra presupuesto',
        text: 'Dirección ve cada obra con lo gastado frente a lo presupuestado, sin esperar al cierre de mes.',
      },
      {
        time: 'Fin de mes',
        title: 'Certificación sin sorpresas',
        text: 'Lo ejecutado ya está registrado: certificar y facturar es cuestión de minutos.',
      },
    ],
    case: {
      title:
        'Una constructora controla el coste real de cada obra sin perseguir albaranes.',
      text: 'Para una empresa de obra y reformas con varios equipos en calle montamos partes de obra, proyectos y control de costes. Cada parte firmado en el tajo actualiza el coste de la obra, y la dirección lo compara con el presupuesto en cualquier momento.',
      tags: 'Partes de trabajo · Proyectos · Control de costes',
      caseSlug: 'gestor-obras-construccion',
      imageHint: 'Captura real: panel de obra con costes',
    },
    images: {
      listado: asset('partes-trabajo.png'),
      hero: asset('partes-trabajo-2.png'),
      caso: asset('proyectos.png'),
    },
    ctaTitle: 'Tu cuenta de prueba, con la obra bajo control.',
    presetMatchers: [/parte/i, /proyecto|obra/i],
  },

  // ── Servicios técnicos e instaladores (plantilla) ──────────────────────
  {
    slug: 'servicios-tecnicos',
    sector: 'Servicios técnicos e instaladores',
    featured: true,
    listingTagline:
      'El técnico empieza el día con su ruta en el móvil: presupuesto, trabajo aprobado y firma del cliente al terminar. La oficina factura sin esperar a que vuelva.',
    listingImageHint: 'Captura real: ruta del día del técnico',
    heroTitle: 'El técnico termina. La factura ya está en marcha.',
    heroSubtitle:
      'Ruta del día, presupuesto en el sitio y firma del cliente desde el móvil. Todo queda sincronizado con la oficina al momento.',
    heroImageHint: 'Captura real: la app en el móvil del técnico',
    modules: [
      {
        label: 'Partes de trabajo',
        price: M,
        color: C_PARTES,
        desc: 'Órdenes de trabajo, materiales usados y horas por servicio, desde el móvil del técnico.',
        complement: {
          label: 'OCR y firma digital',
          price: K,
          color: C_PARTES,
          desc: 'El cliente firma en pantalla y el parte queda cerrado en el sitio.',
        },
      },
    ],
    timeline: [
      {
        time: '08:00',
        title: 'La ruta, en el móvil',
        text: 'Cada técnico ve sus avisos del día, el cliente y el historial de visitas anteriores.',
      },
      {
        time: '11:30',
        title: 'Presupuesto en el sitio',
        text: 'Genera el presupuesto delante del cliente y lo convierte en trabajo aprobado.',
      },
      {
        time: '16:45',
        title: 'Firma al terminar',
        text: 'Materiales registrados y firma del cliente en pantalla. Nada pendiente de pasar a limpio.',
      },
      {
        time: '17:00',
        title: 'La oficina factura',
        text: 'Todo está sincronizado: se factura sin esperar a que el equipo vuelva.',
      },
    ],
    case: {
      title:
        'Una empresa de instalaciones factura el mismo día lo que antes esperaba a fin de semana.',
      text: 'Para una empresa de climatización y fontanería con equipos en campo montamos rutas, presupuestos y firma en el sitio. El día empieza con la ruta asignada en el móvil y termina con los trabajos ya listos para facturar.',
      tags: 'Partes de trabajo · Seguimiento de tareas pendientes de cobro',
      caseSlug: 'app-rutas-instaladores',
      imageHint: 'Captura real: presupuesto y firma en campo',
    },
    images: {
      listado: asset('ubicaciones.png'),
      hero: asset('tareas.png'),
      caso: asset('tareas-por-cobrar.png'),
    },
    ctaTitle: 'Tu cuenta de prueba, con esto ya activo.',
    presetMatchers: [/parte/i, /ocr|firma|escaneo/i],
  },

  // ── Gestorías y asesorías ──────────────────────────────────────────────
  {
    slug: 'gestorias-asesorias',
    sector: 'Gestorías y asesorías',
    featured: true,
    listingTagline:
      'Expedientes centralizados, plazos que avisan solos y clientes que consultan y firman desde casa. Menos llamadas, menos olvidos.',
    listingImageHint: 'Captura real: expediente con plazos y avisos',
    heroTitle: 'Los plazos avisan solos. Los clientes se atienden a sí mismos.',
    heroSubtitle:
      'Expedientes con tareas y vencimientos que no se te pasan, y un portal donde el cliente consulta y firma sin llamarte.',
    heroImageHint: 'Captura real: el portal del cliente y los expedientes',
    modules: [
      {
        label: 'Portal del cliente',
        price: M,
        color: C_PORTAL,
        desc: 'Cada cliente consulta sus documentos, sube lo que le pides y firma desde casa. Sin cadenas de correos.',
      },
      {
        label: 'Proyectos',
        price: M,
        color: C_PROYECTOS,
        desc: 'Cada expediente con sus tareas y plazos. Los vencimientos avisan solos antes de que se echen encima.',
      },
    ],
    timeline: [
      {
        time: '09:00',
        title: 'El día, por vencimientos',
        text: 'Cada expediente muestra sus plazos; los que aprietan aparecen primero, sin repasar agendas.',
      },
      {
        time: '11:00',
        title: 'El cliente aporta sin llamar',
        text: 'Sube su documentación al portal y queda enganchada a su expediente automáticamente.',
      },
      {
        time: '13:30',
        title: 'Firma desde casa',
        text: 'Manda a firmar y el cliente lo resuelve desde el móvil, sin desplazamientos.',
      },
      {
        time: '18:00',
        title: 'Nada pendiente de recordar',
        text: 'Lo que vence mañana ya está avisado. El equipo cierra el día sin post-its.',
      },
    ],
    case: {
      title:
        'Una gestoría deja de perseguir a sus clientes por email para cada trámite.',
      text: 'Para una asesoría con cientos de expedientes montamos un portal de cliente y un seguimiento de plazos. El cliente consulta, aporta y firma desde casa; los vencimientos avisan solos y nada se queda en un cajón.',
      tags: 'Portal del cliente · Proyectos y plazos',
      caseSlug: 'portal-expedientes-gestoria',
      imageHint: 'Captura real: expediente con checklist de plazos',
    },
    images: {
      listado: asset('calendario.png'),
      hero: asset('clientes.png'),
      caso: asset('backlog.png'),
    },
    ctaTitle: 'Tu cuenta de prueba, con los expedientes al día.',
    presetMatchers: [/portal/i, /proyecto|expediente|tarea/i],
  },

  // ── Comercio ───────────────────────────────────────────────────────────
  {
    slug: 'comercio',
    sector: 'Comercio',
    featured: false,
    listingTagline: 'Caja, stock y facturación en el mismo sitio.',
    listingImageHint: 'Captura real: TPV y control de stock',
    heroTitle:
      'Cobras en el mostrador. El stock y la factura se ponen al día solos.',
    heroSubtitle:
      'TPV, inventario y facturación en el mismo sitio: cada venta descuenta existencias y queda lista para facturar.',
    heroImageHint: 'Captura real: la caja y el inventario',
    modules: [
      {
        label: 'TPV',
        price: M,
        color: C_TPV,
        desc: 'Cobra en el mostrador y cada venta descuenta stock y queda lista para facturar.',
      },
      {
        label: 'Producto e inventario',
        price: M,
        color: C_INVENTARIO,
        desc: 'Productos, existencias y entradas de mercancía siempre al día, sin recuentos a ciegas.',
      },
    ],
    timeline: [
      {
        time: 'Apertura',
        title: 'Caja lista',
        text: 'Abres el TPV y ves el stock del día; lo que está bajo mínimos salta a la vista.',
      },
      {
        time: 'En venta',
        title: 'Vender y descontar, a la vez',
        text: 'Cada cobro descuenta existencias al momento: el inventario no se queda atrás.',
      },
      {
        time: 'Reposición',
        title: 'Entrada de mercancía',
        text: 'Registras lo que llega y el stock se actualiza sin recuentos manuales.',
      },
      {
        time: 'Cierre',
        title: 'Cuadre sin Excel',
        text: 'La caja cuadra sola y las ventas del día están listas para facturar.',
      },
    ],
    case: {
      title:
        'Un comercio deja de cuadrar la caja a mano y de recontar el almacén.',
      text: 'Con TPV e inventario conectados, cada venta descuenta stock y alimenta la facturación. El cierre de caja cuadra solo y las existencias reflejan la realidad, sin recuentos a ciegas.',
      tags: 'TPV · Producto e inventario',
      imageHint: 'Captura real: ticket y descuento de stock',
    },
    images: {
      hero: asset('ts-prodcutos.png'),
      caso: asset('productos-2.png'),
    },
    ctaTitle: 'Tu cuenta de prueba, con la caja y el stock conectados.',
    presetMatchers: [/tpv|comand|caja|venta/i, /producto|inventario|stock/i],
  },

  // ── Trasteros y self-storage ───────────────────────────────────────────
  {
    slug: 'trasteros-self-storage',
    sector: 'Trasteros y self-storage',
    featured: false,
    listingTagline: 'Reservas, contratos y accesos sin trabajo manual.',
    listingImageHint: 'Captura real: ocupación y contratos',
    heroTitle: 'El trastero se reserva y se firma solo. Tú ves la ocupación.',
    heroSubtitle:
      'Reservas online, contratos firmados desde casa y una web conectada a tu ocupación real. El alta deja de ser trabajo manual.',
    heroImageHint: 'Captura real: plano de ocupación y contrato',
    modules: [
      {
        label: 'Portal del cliente',
        price: M,
        color: C_PORTAL,
        desc: 'El cliente reserva, firma su contrato y consulta sus recibos sin pasar por la oficina.',
      },
      {
        label: 'Web y CMS',
        price: M,
        color: C_WEB,
        desc: 'Tu web de captación conectada: la disponibilidad que se ve fuera es la real.',
      },
    ],
    timeline: [
      {
        time: 'Desde la web',
        title: 'El cliente reserva su box',
        text: 'Ve la disponibilidad real, elige tamaño y reserva sin llamar a nadie.',
      },
      {
        time: 'En minutos',
        title: 'Contrato firmado',
        text: 'Firma su contrato desde el móvil y el box queda asignado automáticamente.',
      },
      {
        time: 'Cada mes',
        title: 'Cuotas sin perseguir',
        text: 'Los recibos recurrentes se emiten solos; el cliente consulta los suyos en el portal.',
      },
      {
        time: 'En un vistazo',
        title: 'Ocupación al día',
        text: 'Ves qué boxes están libres, ocupados o por vencer, sin cruzar hojas de cálculo.',
      },
    ],
    case: {
      title:
        'Un centro de trasteros llena boxes desde la web mientras el equipo atiende.',
      text: 'Para un self-storage montamos reservas online, contratos firmados a distancia y una web enganchada a la ocupación real. El cliente se da de alta solo y la oficina se dedica a lo que no puede automatizarse.',
      tags: 'Portal del cliente · Web y CMS · Cuotas recurrentes',
      caseSlug: 'gestion-trasteros-self-storage',
      imageHint: 'Captura real: plano de boxes con estados',
    },
    images: {},
    ctaTitle: 'Tu cuenta de prueba, con las reservas ya funcionando.',
    presetMatchers: [/portal/i, /web|cms|contenido|reserva/i],
  },

  // ── Restauración ───────────────────────────────────────────────────────
  {
    slug: 'restauracion',
    sector: 'Restauración',
    featured: false,
    listingTagline: 'Sala, reservas y comandas conectadas con tu facturación.',
    listingImageHint: 'Captura real: sala y comandas',
    heroTitle: 'La comanda entra en sala. La facturación ya la conoce.',
    heroSubtitle:
      'Reservas, mesas y comandas conectadas con la facturación: el servicio fluye y las cuentas salen solas.',
    heroImageHint: 'Captura real: la comanda en sala',
    modules: [
      {
        label: 'Restauración',
        price: M,
        color: C_RESTAURACION,
        desc: 'Reservas, mesas y comandas en un mismo sitio, y cada servicio conectado con tu facturación.',
      },
    ],
    timeline: [
      {
        time: 'Antes del servicio',
        title: 'La sala, montada',
        text: 'Ves reservas y mesas del turno y repartes la sala antes de abrir.',
      },
      {
        time: 'En servicio',
        title: 'Comanda a cocina',
        text: 'El pedido entra desde la mesa y llega a cocina sin idas y venidas.',
      },
      {
        time: 'A la cuenta',
        title: 'Sin recontar',
        text: 'La cuenta se arma sola con lo servido; dividir o cobrar por mesa es directo.',
      },
      {
        time: 'Cierre',
        title: 'Caja y facturación cuadradas',
        text: 'El servicio del día queda registrado y listo para facturar, sin pasar nada a limpio.',
      },
    ],
    case: {
      title:
        'Un restaurante conecta la sala con la facturación y deja de recontar tickets.',
      text: 'Para un local con sala y reservas montamos comandas y control de mesas conectados con la facturación. El pedido entra desde la mesa, la cuenta se arma sola y el cierre cuadra sin Excel.',
      tags: 'Restauración · Reservas y comandas',
      caseSlug: 'sistema-reservas-restaurante',
      imageHint: 'Captura real: comanda y plano de mesas',
    },
    images: {},
    ctaTitle: 'Tu cuenta de prueba, con la sala conectada.',
    presetMatchers: [/restaura|comand|tpv|sala|mesa|reserva/i],
  },
];

/** Texto común del CTA final del detalle (el titular cambia por solución). */
export const SOLUTION_CTA_TEXT =
  'Arrancas con estos módulos funcionando desde el primer minuto. Y si prefieres que te lo montemos, hablas con quien lo ha construido.';

/** Huecos de imagen de una solución. */
export type SolutionImageSlot = keyof SolutionImages;

/** Busca una solución por slug. */
export function findSolution(slug: string): Solution | undefined {
  return SOLUTIONS.find(s => s.slug === slug);
}

/** Piezas del icono de una solución: un `tr` por módulo (y su complemento en contorno). */
export function solutionPieces(
  s: Solution
): { color: string; outline: boolean }[] {
  const list: { color: string; outline: boolean }[] = [];
  for (const m of s.modules) {
    list.push({ color: m.color, outline: false });
    if (m.complement) list.push({ color: m.complement.color, outline: true });
  }
  return list;
}

/** Precio total €/mes de una solución (base + módulos + complementos). */
export function solutionTotal(s: Solution): number {
  return s.modules.reduce(
    (sum, m) => sum + m.price + (m.complement?.price ?? 0),
    SOLUTION_BASE.price
  );
}

/** Desglose legible: "Base 45 € + Partes 8 € + OCR y firma 3 €". */
export function solutionBreakdown(s: Solution): string {
  const parts = [`Base ${SOLUTION_BASE.price} €`];
  for (const m of s.modules) {
    parts.push(`${shortLabel(m.label)} ${m.price} €`);
    if (m.complement) parts.push(`${shortLabel(m.complement.label)} ${m.complement.price} €`);
  }
  return parts.join(' + ');
}

/** Acorta etiquetas largas para el desglose ("OCR y firma digital" → "OCR y firma"). */
function shortLabel(label: string): string {
  return label.replace(/\s+digital$/i, '');
}
